import React, { useCallback, useContext, useEffect, useState } from "react";
import { useMedia } from "react-media";
import { useHistory } from "react-router-dom";
import moment from "moment";

import { BookingActionsContext, BookingDataContext } from "containers/data_context";

import routes from "routing/routes";
import HotelRatesSection from "components/hotel_rates_section/hotel_rates_section";
import MEDIA_QUERIES from "constants/media_queries";
import buildPath from "utils/build_path";
import calculateSummaryParams from "utils/calculate_summary_params";

import DateSelect from "./date_select";
import MinPricePanel from "./min_price_panel";
import MobileSummary from "./mobile_summary";
import OccupancySetting from "./occupancy_settings";
import Summary from "./summary";

import styles from "./search_section.module.css";
import useHandleFirebaseEvents from "hooks/useHandleFirebaseEvents";
import date_formatter from "utils/date_formatter";

export default function SearchSection({
  update,
  data = null,
  bookedRooms,
  disable,
  bookedRatesOccupancy,
  propertyInfo,
  userData,
}) {
  const { registerSearchEvent } = useHandleFirebaseEvents();
  const [selectedRatesByRoom, setSelectedRatesByRoom] = useState({});
  const [missingSpaces, setMissingSpaces] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const bookingData = useContext(BookingDataContext);
  const { channelId, params, property, roomsInfo, bestOffer } = bookingData;
  const { setParams, loadRoomsInfo, saveDataToStorage, clearDataFromStorage } =
    useContext(BookingActionsContext);
  const matchedQueries = useMedia({ queries: MEDIA_QUERIES });
  const history = useHistory();
  const isMobile = matchedQueries.xs || matchedQueries.sm || matchedQueries.md;
  const { data: propertyRooms, isLoading } = roomsInfo;
  const { data: propertyData, isLoading: propertyLoading } = property;

  const { ratesOccupancyPerRoom, currency, checkinDate, checkoutDate, adults, children } = params;

  const isRateSelected = Boolean(Object.keys(selectedRatesByRoom).length);
  const isDatesSelected = moment(checkinDate).isValid() && moment(checkoutDate).isValid();

  const handleBook = useCallback(() => {
    saveDataToStorage(bookingData);

    const paymentPagePath = buildPath(history.location.search, routes.paymentPage, { channelId });

    history.push(paymentPagePath);
  }, [bookingData, saveDataToStorage, history, channelId]);

  const handleSearch = useCallback(() => {
    clearDataFromStorage();

    registerSearchEvent({
      channel_id: channelId,
      checkin_date: date_formatter.toApi(params?.checkinDate),
      checkout_date: date_formatter.toApi(params?.checkoutDate),
      currency: params?.currency,
      adults: params?.adults,
      children: params?.children,
      max_infant_age: params?.maxInfantAge,
      current_date: date_formatter.toApi(moment()),
      user_id: userData?.id || 0,
    });

    loadRoomsInfo(channelId, params);
  }, [channelId, params, loadRoomsInfo, clearDataFromStorage]);

  const handleSearchChange = useCallback(
    (params) => {
      clearDataFromStorage();

      params.ratesOccupancyPerRoom = {};
      setParams(params);
    },
    [clearDataFromStorage, setParams],
  );
  useEffect(() => {
    if (isDatesSelected && checkinDate && checkoutDate) {
      handleSearch();
    }
  }, [isDatesSelected, checkinDate, checkoutDate, handleSearch]);

  useEffect(
    function setSummaryParams() {
      const summaryParams = calculateSummaryParams(propertyRooms, ratesOccupancyPerRoom);

      if (!summaryParams) {
        return;
      }

      setTotalPrice(summaryParams.total);
      setSelectedRatesByRoom(summaryParams.selectedRatesByRoom);
    },
    [propertyRooms, ratesOccupancyPerRoom],
  );

  useEffect(
    function calculateMissingSpaces() {
      let availableAdultSpaces = 0;
      let availableChildSpaces = 0;

      Object.values(selectedRatesByRoom).forEach((room) => {
        room.selectedRates.forEach((rate) => {
          const { amount, occupancy } = rate;

          availableAdultSpaces += amount * occupancy?.adults;
          availableChildSpaces += amount * occupancy?.children + amount * occupancy?.infants;
        });
      });

      const missingAdults = adults - availableAdultSpaces;
      const missingChildren = children - availableChildSpaces;

      const newMissingSpaces =
        missingAdults > 0 || missingChildren > 0 ? missingAdults + missingChildren : 0;

      setMissingSpaces(newMissingSpaces);
    },
    [selectedRatesByRoom, adults, children],
  );

  const SummaryComponent = isMobile ? MobileSummary : Summary;
  const wrapperClasses = [styles.searchPanelWrapper];

  if (!propertyData.photos.length) {
    wrapperClasses.push(styles.searchPanelNoOffset);
  }

  return (
    <div className="container p-0">
      <div className={styles.wrapper}>
        {update ? (
          <>
            <div className={wrapperClasses.join(" ")}>
              {/* {bestOffer?.offer && <MinPricePanel bestOffer={bestOffer} params={params} />} */}
              <div className={styles.searchSection}>
                <div className="row mb-2">
                  <div className="col-md-5">
                    <DateSelect bookingParams={params} handleSearchChange={handleSearchChange} />
                  </div>
                  <div className="col-md-4">
                    <OccupancySetting
                      property={{ ...propertyData, ...propertyInfo }}
                      bookingParams={params}
                      handleSearchChange={handleSearchChange}
                    />
                    <div className="d-flex justify-content-center">
                      {propertyData?.hotelPolicy?.isAdultsOnly && (
                        <p style={{ margin: 0 }}>
                          <sup>*</sup>Only Adults Allowed
                        </p>
                      )}
                      {!propertyData?.hotelPolicy?.isAdultsOnly &&
                        Boolean(propertyInfo?.children_allowed) && (
                          <p className="mr-5">
                            Child {propertyData?.hotelPolicy?.infantMaxAge + 1}-
                            {propertyData?.hotelPolicy?.childrenMaxAge}
                          </p>
                        )}
                      {!propertyData?.hotelPolicy?.isAdultsOnly &&
                        Boolean(propertyInfo?.infant_allowed) && (
                          <p>Infant 0-{propertyData?.hotelPolicy?.infantMaxAge}</p>
                        )}
                    </div>
                  </div>

                  <div className="col-md-3">
                    <SummaryComponent
                      property={propertyData}
                      selectedRatesByRoom={selectedRatesByRoom}
                      bookingParams={params}
                      isDatesSelected={isDatesSelected}
                      isRateSelected={isRateSelected}
                      totalPrice={totalPrice}
                      missingSpaces={missingSpaces}
                      currency={currency}
                      loading={isLoading}
                      onBook={handleBook}
                      onSearch={handleSearch}
                      update={update}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.floatingSearchContainer}>
              {checkoutDate && checkinDate && (
                <HotelRatesSection
                  bookedRooms={bookedRooms}
                  bookedRatesOccupancy={bookedRatesOccupancy}
                  update={update}
                  disable={disable}
                  loading={propertyLoading}
                />
              )}
            </div>
          </>
        ) : (
          <div className={styles.stickyContainer}>
            <div className={wrapperClasses.join(" ")}>
              {/* {bestOffer?.offer && propertyRooms?.length > 0 && (
                <MinPricePanel bestOffer={bestOffer} params={params} />
              )} */}
              <div className={styles.searchSection}>
                <div className="mb-2">
                  <div className="mt-2">
                    <DateSelect bookingParams={params} handleSearchChange={handleSearchChange} />
                  </div>
                  <div className=" mt-5">
                    <OccupancySetting
                      property={{ ...propertyData, ...propertyInfo }}
                      bookingParams={params}
                      handleSearchChange={handleSearchChange}
                    />
                    <div className="d-flex justify-content-center">
                      {propertyData?.hotelPolicy?.isAdultsOnly && (
                        <p style={{ margin: 0 }}>
                          <sup>*</sup>Only Adults Allowed
                        </p>
                      )}
                      {!propertyData?.hotelPolicy?.isAdultsOnly &&
                        Boolean(propertyInfo?.children_allowed) && (
                          <p className="mr-5">
                            Child {propertyInfo?.child_min_age}-
                            {propertyData?.hotelPolicy?.childrenMaxAge}
                          </p>
                        )}
                      {!propertyData?.hotelPolicy?.isAdultsOnly &&
                        Boolean(propertyInfo?.infant_allowed) && (
                          <p>Infant 0-{propertyData?.hotelPolicy?.infantMaxAge}</p>
                        )}
                    </div>
                  </div>

                  <div className="mt-5">
                    <SummaryComponent
                      selectedRatesByRoom={selectedRatesByRoom}
                      bookingParams={params}
                      isDatesSelected={isDatesSelected}
                      isRateSelected={isRateSelected}
                      totalPrice={totalPrice}
                      missingSpaces={missingSpaces}
                      currency={currency}
                      loading={isLoading}
                      onBook={handleBook}
                      onSearch={handleSearch}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
